import React from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { id } from 'date-fns/locale'
import clsx from 'clsx'
import Card from '@/components/ui/Card'
import Badge from '@/components/ui/Badge'
import ProgressBar from '@/components/ui/ProgressBar'
import type { Kegiatan } from '@/types/pocketbase'

interface KegiatanCardProps {
  kegiatan: Kegiatan
  tahapCount?: number
  selesaiCount?: number
  pesertaCount?: number
  pjName?: string
}

const statusVariant: Record<string, 'primary' | 'green' | 'yellow' | 'red'> = {
  aktif: 'green',
  selesai: 'primary',
  draft: 'yellow',
  batal: 'red',
}

const statusLabel: Record<string, string> = {
  aktif: 'Aktif',
  selesai: 'Selesai',
  draft: 'Draft',
  batal: 'Batal',
}

const jenisLabel: Record<string, string> = {
  semper: 'Semper',
  seminar: 'Seminar',
  pelatihan: 'Pelatihan',
  musyawarah: 'Musyawarah',
  ribut: 'Rapat',
  lainnya: 'Lainnya',
}

export default function KegiatanCard({
  kegiatan,
  tahapCount = 12,
  selesaiCount = 0,
  pesertaCount = 0,
  pjName,
}: KegiatanCardProps) {
  const navigate = useNavigate()
  const progress = tahapCount > 0 ? Math.round((selesaiCount / tahapCount) * 100) : 0

  return (
    <Card
      className="mb-3"
      onClick={() => navigate(`/kegiatan/${kegiatan.id}`)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex gap-1.5 flex-wrap">
          <Badge variant={statusVariant[kegiatan.status] || 'gray'}>
            {statusLabel[kegiatan.status] || kegiatan.status}
          </Badge>
          <Badge variant="blue">{jenisLabel[kegiatan.jenis] || kegiatan.jenis}</Badge>
        </div>
      </div>

      <h3 className="text-sm font-bold text-text-primary mb-1 leading-snug">
        {kegiatan.judul}
      </h3>

      {kegiatan.tema && (
        <p className="text-xs text-text-secondary mb-2 italic">{kegiatan.tema}</p>
      )}

      <div className="space-y-1 mb-3">
        <div className="flex items-center gap-1.5 text-xs text-text-secondary">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>
            {format(new Date(kegiatan.tanggal_mulai), 'dd MMM yyyy', { locale: id })}
            {kegiatan.tanggal_selesai !== kegiatan.tanggal_mulai && (
              <> — {format(new Date(kegiatan.tanggal_selesai), 'dd MMM yyyy', { locale: id })}</>
            )}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-text-secondary">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="truncate">{kegiatan.lokasi}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-text-secondary">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span>{pesertaCount} peserta</span>
        </div>
      </div>

      <div className="mb-2">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-text-secondary">Progress</span>
          <span className="text-xs font-semibold text-primary">{progress}%</span>
        </div>
        <ProgressBar value={selesaiCount} max={tahapCount} size="sm" />
      </div>

      {pjName && (
        <p className="text-xs text-text-secondary">
          <span className="font-medium">PJ:</span> {pjName}
        </p>
      )}
    </Card>
  )
}
